import { useEffect, useState } from 'react';
import { Box, BoxProps, Button, Tooltip } from '@bitrise/bitkit';
import useBranches from './useBranches';
import { BranchManualInput } from './BranchManualInput';
import { BranchSelectDropdown } from './BranchSelectDropdown';

export interface BranchSelectorProps extends Omit<BoxProps, 'onSelect'> {
  helperText?: string;
  infoTooltipLabel?: string;
  isQueryEnabled: boolean;
  isRequired?: boolean;
  label: string;
  onError?: () => void;
  onSelect: (selectedBranchName: string, isValid: boolean) => void;
  selectedBranch?: string;
  slug: string;
}

const BranchSelector = (props: BranchSelectorProps) => {
  const {
    helperText,
    infoTooltipLabel,
    isQueryEnabled,
    isRequired,
    label,
    onError,
    onSelect,
    selectedBranch,
    slug,
    ...rest
  } = props;
  const { data, error, isError, isLoading } = useBranches(slug || '', {
    retry: 1,
    enabled: isQueryEnabled,
  });

  const [isSelectorMode, setIsSelectorMode] = useState(false);

  let isSelectorAvailable = false;
  if (!isLoading && data?.branches && data?.branches.length > 0) {
    isSelectorAvailable = true;
  }

  useEffect(() => {
    if (isSelectorMode && !selectedBranch && data?.branches && data.branches.length > 0) {
      onSelect(data.branches[0], true);
    }
  }, [data?.branches, isSelectorMode, onSelect, selectedBranch]);

  useEffect(() => {
    if (onError && isError) {
      onError();
    }
  }, [isError, onError]);

  let tooltipLabel = '';

  if (isError && error?.request.status === 404) {
    tooltipLabel = 'You don’t have access to branches';
  } else if (!isError && (data?.branches === undefined || data?.branches.length === 0)) {
    tooltipLabel = 'We couldn’t detect any branches';
  }

  const onChange = (newBranchValue: string, isValid: boolean) => {
    onSelect(newBranchValue, isValid);
  };

  return (
    <Box display="flex" gap="8" alignItems="flex-start" width="100%" {...rest}>
      {isSelectorMode ? (
        <BranchSelectDropdown
          branches={data?.branches || []}
          helperText={helperText}
          infoTooltipLabel={infoTooltipLabel}
          isLoading={isLoading}
          label={label}
          value={selectedBranch}
          onSelect={(selectedBranchName) => onChange(selectedBranchName || '', Boolean(selectedBranchName))}
          disabled={!data?.branches || data?.branches.length === 0}
          isRequired={isRequired}
        />
      ) : (
        <BranchManualInput
          helperText={helperText}
          infoTooltipLabel={infoTooltipLabel}
          isLoading={isLoading}
          isRequired={isRequired}
          label={label}
          onChange={onChange}
          value={selectedBranch}
        />
      )}
      <Tooltip isDisabled={!tooltipLabel} label={tooltipLabel}>
        <Button
          flexShrink="0"
          isDisabled={!isSelectorAvailable}
          isLoading={isLoading}
          marginBlockStart="24"
          onClick={() => {
            setIsSelectorMode(!isSelectorMode);
          }}
          variant="secondary"
          width="10rem"
        >
          {isSelectorMode ? 'Custom input' : 'Select branch'}
        </Button>
      </Tooltip>
    </Box>
  );
};

export default BranchSelector;
